import { Save } from "lucide-react";
import { useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, newIdempotencyKey } from "../api";
import { PageBackLink, PageHeader, StateView } from "../components";
import { invalidateApiData, useApiData } from "../hooks";
import type { CursorPage, ExpenseCategory, FinancialAccount, WorkOrder } from "../types";

const normalizeMoney = (raw: string) => {
  const clean = raw.trim().replace(/\s/g, "").replace("R$", "").replace(/\.(?=\d{3}(?:\D|$))/g, "").replace(",", ".");
  if (!/^\d+(?:\.\d{0,2})?$/.test(clean)) return null;
  const [whole, fraction = ""] = clean.split(".");
  return `${whole}.${fraction.padEnd(2, "0")}`;
};

export function NewFinanceEntryPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const today = useMemo(() => new Date().toLocaleDateString("sv-SE", { timeZone: "America/Sao_Paulo" }), []);
  const [accountId, setAccountId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [workOrderId, setWorkOrderId] = useState(searchParams.get("workOrderId") ?? "");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(today);
  const [description, setDescription] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const accounts = useApiData<FinancialAccount[]>("/financial-accounts");
  const categories = useApiData<ExpenseCategory[]>("/expense-categories");
  const selectedCategory = categories.data?.find(({ id }) => id === categoryId);
  const selectedAccount = accounts.data?.find(({ id }) => id === accountId);
  const workOrders = useApiData<CursorPage<WorkOrder>>(selectedCategory?.nature === "direct" ? "/work-orders?pageSize=100" : null);
  const filteredWorkOrders = useMemo(() => {
    const orders = workOrders.data?.data ?? [];
    if (!selectedAccount?.branchId) return orders;
    return orders.filter((order) => !order.branchId || order.branchId === selectedAccount.branchId);
  }, [workOrders.data, selectedAccount]);

  const submit = async (event: FormEvent) => {
    event.preventDefault(); setError("");
    const exactAmount = normalizeMoney(amount);
    if (!exactAmount) return setError("Revise o valor informado.");
    if (selectedCategory?.nature === "direct" && !workOrderId) return setError("Despesa direta exige OS.");
    setBusy(true);
    try {
      if (!accountId || !selectedCategory) throw new Error("Selecione conta e categoria.");
      await api("/expenses/manual", { method: "POST", headers: { "Idempotency-Key": newIdempotencyKey() }, body: JSON.stringify({
        financialAccountId: accountId, categoryId, description, nature: selectedCategory.nature,
        amount: exactAmount, competenceDate: date,
        workOrderId: selectedCategory.nature === "direct" ? workOrderId : undefined,
      }) });
      invalidateApiData("/expenses");
      invalidateApiData("/finance/summary");
      invalidateApiData("/finance/movements");
      navigate("/financeiro", { replace: true });
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Lançamento não concluído."); }
    finally { setBusy(false); }
  };

  const loading = accounts.loading || categories.loading || (selectedCategory?.nature === "direct" ? workOrders.loading : false);
  const loadError = accounts.error || categories.error || (selectedCategory?.nature === "direct" ? workOrders.error : "");
  return <>
    <section className="page-intro"><div className="app-container"><PageBackLink to="/financeiro" context="Financeiro" /><PageHeader title="Novo lançamento" /></div></section>
    <section className="app-section compact"><div className="app-container appointment-form-shell"><StateView loading={loading} error={loadError}>
      <form className="appointment-form" onSubmit={submit}>
        <section className="form-section"><div className="form-grid">
          <label>Conta<select value={accountId} onChange={(event) => { setAccountId(event.target.value); setWorkOrderId(""); }} required><option value="">Selecione</option>{accounts.data?.map((account) => <option key={account.id} value={account.id}>{account.name}</option>)}</select></label>
          <label>Categoria<select value={categoryId} onChange={(event) => { setCategoryId(event.target.value); setWorkOrderId(""); }} required><option value="">Selecione</option>{categories.data?.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}</select></label>
          <label>Valor<input inputMode="decimal" value={amount} onChange={(event) => setAmount(event.target.value)} placeholder="0,00" required /></label>
          <label>Competência<input type="date" value={date} onChange={(event) => setDate(event.target.value)} required /></label>
          {selectedCategory?.nature === "direct" && <label className="field-span-2">Ordem de serviço<select value={workOrderId} onChange={(event) => setWorkOrderId(event.target.value)} required><option value="">Selecione</option>{filteredWorkOrders.map((order) => <option key={order.id} value={order.id}>OS {order.number}</option>)}</select></label>}
          <label className="field-span-2">Descrição<input value={description} onChange={(event) => setDescription(event.target.value)} maxLength={500} required /></label>
        </div></section>
        {error && <div className="form-error" role="alert">{error}</div>}
        <footer className="form-actions"><Link className="site-button site-button-outline" to="/financeiro">Cancelar</Link><button className="site-button site-button-navy" disabled={busy}><Save size={17} />{busy ? "Salvando…" : "Salvar"}</button></footer>
      </form>
    </StateView></div></section>
  </>;
}
